'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import { Info, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { ChipsInput } from '@/components/forms/chips-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface DataTableChipsFilterProps<TData extends RowData> {
  column: Column<TData>;
  isFilterPending?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  maxChips?: number;
  maxLength?: number;
}

function DataTableChipsFilter<TData extends RowData>({
  column,
  isFilterPending,
  placeholder,
  hint,
  className,
  maxChips = 20,
  maxLength = 100
}: DataTableChipsFilterProps<TData>) {
  const tc = useTranslations('common');
  const raw = column.getFilterValue();
  const value: string[] = Array.isArray(raw)
    ? (raw as string[])
    : typeof raw === 'string' && raw.length > 0
      ? [raw]
      : [];

  const tooltipText = hint ?? placeholder;
  const showHelpIcon = Boolean(hint);

  const input = (
    <div className='flex items-center gap-1'>
      <ChipsInput
        size='sm'
        value={value}
        onChange={(next) =>
          column.setFilterValue(next.length ? next : undefined)
        }
        placeholder={placeholder}
        maxChips={maxChips}
        maxLength={maxLength}
        className={cn('w-full max-w-sm min-w-40 lg:min-w-56', className)}
        ariaLabel={placeholder}
        onMaxChipsReached={(max) => toast.error(tc('chipMaxReached', { max }))}
        onMaxLengthExceeded={(max) => toast.error(tc('chipMaxLength', { max }))}
        rightSlot={
          isFilterPending ? (
            <Loader2 className='text-muted-foreground size-4 animate-spin' />
          ) : null
        }
      />
      {showHelpIcon && (
        <span
          aria-label={tc('searchHelp')}
          className='text-muted-foreground inline-flex h-8 w-8 items-center justify-center'
        >
          <Info className='size-4' />
        </span>
      )}
    </div>
  );

  if (!tooltipText) {
    return input;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{input}</TooltipTrigger>
      <TooltipContent className='max-w-sm text-start whitespace-normal'>
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

export { DataTableChipsFilter };
